import { AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import axiosJC261 from 'src/app/Service/axiosJC261';
import { GurdServiceService } from 'src/app/Service/gurd-service.service';
import { NotificationService } from 'src/app/Service/notification.service';
import { OtherService } from 'src/app/Service/other.service';
import { ImageRecord, MonitoringImage } from './cctv-monitoring.models';
import { normalizeImage } from './cctv-monitoring.utils';

@Component({
  selector: 'app-cctv-monitoring',
  templateUrl: './cctv-monitoring.component.html',
  styleUrls: ['./cctv-monitoring.component.css'],
})
export class CctvMonitoringComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('gallery') gallery!: ElementRef<HTMLElement>;
  @ViewChild('workspace') workspace!: ElementRef<HTMLElement>;
  @ViewChild('statusLine') statusLine!: ElementRef<HTMLElement>;
  readonly sizeChoices = [1, 2, 4, 6, 8, 12, 16];
  showStats = false;
  category: 'cabin' | 'outside' = 'cabin';
  images: MonitoringImage[] = [];
  search = '';
  page = 1;
  pageSize = 4;
  gridColumns = 2;
  gridRows = 2;
  readonly pinned = new Set<string>();
  rotationSeconds = 10;
  paused = false;
  selectedImage: MonitoringImage | null = null;
  private rotationTimer?: ReturnType<typeof setInterval>;
  private resizeTimer?: ReturnType<typeof setTimeout>;
  private automaticPageSize = true;
  private fittingPageSizes = this.sizeChoices;
  lastUpdated: Date | null = null;
  hasLoaded = false;
  loading = false;
  loadError = '';

  constructor(
    private guard: GurdServiceService,
    private notification: NotificationService,
    private otherService: OtherService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.loadImages();
    this.restartRotation();
  }

  ngOnDestroy(): void {
    if (this.rotationTimer) clearInterval(this.rotationTimer);
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
  }

  ngAfterViewInit(): void {
    this.schedulePageSizeUpdate();
  }

  @HostListener('window:resize')
  schedulePageSizeUpdate(): void {
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => this.updatePageSize(), 100);
  }

  toggleStats(): void {
    this.showStats = !this.showStats;
    this.schedulePageSizeUpdate();
  }

  private updatePageSize(): void {
    if (!this.gallery || !this.workspace || !this.statusLine) return;
    const gallery = this.gallery.nativeElement;
    const width = Math.max(1, gallery.clientWidth - 24);
    const height = Math.max(1, window.innerHeight - gallery.getBoundingClientRect().top -
      this.workspace.nativeElement.offsetHeight - this.statusLine.nativeElement.offsetHeight - 92);
    this.fittingPageSizes = this.sizeChoices.filter(size => this.findLayout(size, width, height, true));
    if (!this.fittingPageSizes.length) this.fittingPageSizes = [1];
    const minimum = this.sizeChoices.find(size => size >= this.pinned.size) || 16;
    const options = this.fittingPageSizes.filter(size => size >= minimum);
    if (!options.length) options.push(minimum);
    const desired = width * height / (400 * 260);
    const nextSize = this.automaticPageSize ? options.reduce((best, size) =>
      Math.abs(Math.log(size / desired)) < Math.abs(Math.log(best / desired)) ? size : best, options[0])
      : options.includes(this.pageSize) ? this.pageSize : options.filter(size => size <= this.pageSize).pop() || options[0];
    if (nextSize !== this.pageSize) {
      this.pageSize = nextSize;
      this.page = 1;
    }
    const layout = this.findLayout(this.pageSize, width, height, true) || this.findLayout(this.pageSize, width, height, false);
    if (layout) { this.gridColumns = layout.columns; this.gridRows = layout.rows; }
  }

  private findLayout(count: number, width: number, height: number, requireFit: boolean): { columns: number; rows: number } | null {
    let best: { columns: number; rows: number; score: number } | null = null;
    for (let columns = 1; columns <= count; columns++) {
      const rows = Math.ceil(count / columns);
      const tileWidth = (width - (columns - 1) * 8) / columns;
      const tileHeight = (height - (rows - 1) * 8) / rows;
      if (tileWidth <= 0 || tileHeight <= 0 || (requireFit && (tileWidth < 190 || tileHeight < 244))) continue;
      const score = -Math.abs(Math.log((tileWidth / tileHeight) / 1.55)) - (columns * rows - count) * .09;
      if (!best || score > best.score) best = { columns, rows, score };
    }
    return best;
  }

  get filteredImages(): MonitoringImage[] {
    const query = this.search.trim().toLowerCase();
    return this.images.filter(image => image.category === this.category &&
      (!query || [image.name, image.plate, image.unitId].some(value => value.toLowerCase().includes(query))));
  }

  get visibleImages(): MonitoringImage[] {
    const pinned = this.filteredImages.filter(image => this.pinned.has(image.unitId)).slice(0, this.pageSize);
    const others = this.filteredImages.filter(image => !this.pinned.has(image.unitId));
    const slots = this.pageSize - pinned.length;
    return [...pinned, ...others.slice((this.page - 1) * slots, this.page * slots)];
  }

  get pageCount(): number {
    const pinned = this.filteredImages.filter(image => this.pinned.has(image.unitId)).length;
    const slots = this.pageSize - pinned;
    return slots > 0 ? Math.max(1, Math.ceil((this.filteredImages.length - pinned) / slots)) : 1;
  }

  get pageSizeOptions(): number[] {
    const options = this.fittingPageSizes.filter(size => size >= this.pinned.size);
    if (!options.includes(this.pageSize)) options.push(this.pageSize);
    return options.sort((a, b) => a - b);
  }

  togglePin(image: MonitoringImage): void {
    if (this.pinned.has(image.unitId)) this.pinned.delete(image.unitId);
    else if (this.pinned.size >= this.pageSize) {
      this.notification.showWarning('تعداد خودروهای پین‌شده نمی‌تواند بیشتر از تعداد تصاویر صفحه باشد.', 'پین تصویر');
      return;
    } else this.pinned.add(image.unitId);
    this.page = 1;
  }

  setPageSize(size: number): void {
    if (size < this.pinned.size) {
      this.notification.showWarning('ابتدا پین خودروهای اضافی را بردارید.', 'تعداد تصاویر');
      return;
    }
    this.pageSize = size;
    this.automaticPageSize = false;
    this.page = 1;
    this.schedulePageSizeUpdate();
  }

  openImage(image: MonitoringImage): void {
    this.selectedImage = image;
  }

  closeImage(): void {
    this.selectedImage = null;
  }

  restartRotation(): void {
    if (this.rotationTimer) clearInterval(this.rotationTimer);
    if (this.rotationSeconds > 0) this.rotationTimer = setInterval(() => {
      if (!this.paused && !this.selectedImage && !this.search.trim() && !document.hidden && this.pageCount > 1) {
        this.page = this.page % this.pageCount + 1;
      }
    }, this.rotationSeconds * 1000);
  }

  async toggleFullscreen(): Promise<void> {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
    } catch {
      this.notification.showError('امکان نمایش تمام‌صفحه وجود ندارد');
    }
  }

  setCategory(category: 'cabin' | 'outside'): void {
    this.category = category;
    this.page = 1;
  }

  async loadImages(): Promise<void> {
    if (this.loading) return;
    this.loading = true;
    this.loadError = '';
    try {
      const response = await axiosJC261.post('GetLatestImages', {
        iSystemUser: Number(this.guard.decrypt(localStorage.getItem('ID'))),
        strSession: this.guard.decrypt(localStorage.getItem('Session')),
      });
      const result =
        typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;
      if (result?.ISSessionExpired === true) {
        this.notification.showError('نشست کاربری شما منقضی شده است');
        this.otherService.removeLocalStorage();
        this.router.navigate(['login'], { queryParams: { error: '1xzOPwsZ' } });
        return;
      }
      if (result?.iResult === 0)
        throw new Error(result.strError || 'دریافت تصاویر ناموفق بود');
      const rows = Array.isArray(result)
        ? result
        : result?.Data ||
          result?.data ||
          result?.Table ||
          result?.rows ||
          result?.images;
      if (!Array.isArray(rows))
        throw new Error('پاسخ دریافت تصاویر معتبر نیست');
      const baseUrl = new URL(axiosJC261.defaults.baseURL || '/', window.location.href).origin;
      const latest = new Map<string, MonitoringImage>();
      for (const row of rows as ImageRecord[]) {
        const image = normalizeImage(row, baseUrl);
        if (!image) continue;
        const key = `${image.unitId}|${image.category}`;
        const previous = latest.get(key);
        if (!previous || (Date.parse(image.date) || 0) > (Date.parse(previous.date) || 0)) latest.set(key, image);
      }
      this.images = Array.from(latest.values());
      this.page = 1;
      this.lastUpdated = new Date();
      this.hasLoaded = true;
    } catch (error) {
      this.loadError =
        error instanceof Error && error.message !== 'Network Error'
          ? error.message
          : 'اتصال به سرور تصاویر برقرار نشد';
      if (this.hasLoaded) this.notification.showError(this.loadError);
    } finally {
      this.loading = false;
    }
  }
}
